/**
 * Vehicle: mesh (from vehicle_mesh templates) + arcade physics + lights + damage.
 *
 * Local space: +Z forward, +Y up, +X = LEFT (driver side). heading is a yaw about +Y,
 * forward = (sin h, 0, cos h), left = (cos h, 0, -sin h). Turning right = heading decreases.
 */
import * as THREE from 'three';
import { G } from './ctx';
import { clamp, damp, lerp, wrapAngle } from './util';
import { CAR_COLORS, getVehicleTemplate, vehicleMats, type VehicleTemplate } from './vehicle_mesh';
import type { DamageSource, IPed, IVehicle, VehicleControls, VehicleSpec, VehicleType } from './types';

let nextId = 1;

export const VEHICLE_SPECS: Record<VehicleType, VehicleSpec> = {
  sedan: { type: 'sedan', length: 4.5, width: 1.9, height: 1.5, maxSpeed: 40, accel: 9, brake: 20, steerLock: 0.6, grip: 0.9, mass: 1400, health: 100, seats: 4 },
  hatch: { type: 'hatch', length: 3.9, width: 1.8, height: 1.5, maxSpeed: 36, accel: 8, brake: 19, steerLock: 0.66, grip: 0.92, mass: 1100, health: 90, seats: 4 },
  sports: { type: 'sports', length: 4.4, width: 1.95, height: 1.2, maxSpeed: 60, accel: 15, brake: 24, steerLock: 0.55, grip: 0.86, mass: 1300, health: 90, seats: 2 },
  suv: { type: 'suv', length: 4.8, width: 2.0, height: 1.9, maxSpeed: 42, accel: 8.5, brake: 18, steerLock: 0.55, grip: 0.9, mass: 2000, health: 140, seats: 4 },
  taxi: { type: 'taxi', length: 4.5, width: 1.9, height: 1.5, maxSpeed: 40, accel: 9, brake: 20, steerLock: 0.6, grip: 0.9, mass: 1400, health: 100, seats: 4 },
  van: { type: 'van', length: 5.2, width: 2.0, height: 2.2, maxSpeed: 34, accel: 6.5, brake: 17, steerLock: 0.5, grip: 0.9, mass: 2200, health: 150, seats: 2 },
  truck: { type: 'truck', length: 8, width: 2.5, height: 3.2, maxSpeed: 28, accel: 4.5, brake: 14, steerLock: 0.45, grip: 0.95, mass: 6000, health: 300, seats: 2 },
  bus: { type: 'bus', length: 11, width: 2.5, height: 3.2, maxSpeed: 26, accel: 3.8, brake: 12, steerLock: 0.4, grip: 0.95, mass: 9000, health: 350, seats: 2 },
  police: { type: 'police', length: 4.5, width: 1.9, height: 1.5, maxSpeed: 50, accel: 12, brake: 23, steerLock: 0.6, grip: 0.92, mass: 1600, health: 130, seats: 4 },
};

// ---------------------------------------------------------------- tuning ----
const MAX_DT = 0.05;
const WALL_TORQUE = 0.22; // fraction of the physical angular impulse applied on wall hits
const CAR_TORQUE = 0.45; // same for car vs car
const RESTITUTION_WALL = 0.18;
const CRASH_MIN = 2.0; // m/s along the normal before a contact counts as a crash (sfx/event)
const RESTITUTION_CAR = 0.25;
const HORN_COOLDOWN = 0.6;
const LAT_ACCEL = 19; // m/s^2 of lateral grip at grip = 1 (arcade-generous, ~2 g)
const HEAD_ON = 1.7; // emissive intensity of headlights when on (> 1 blooms)
const TAIL_ON = 0.55;
const TAIL_BRAKE = 2.0;
const TAIL_REVERSE = 1.4;

// shared temporaries (never allocate in update)
const _v = new THREE.Vector3();
const _tailRed = new THREE.Color(0xff1408);
const _tailWhite = new THREE.Color(0xfff0e0);
const _charred = new THREE.Color(0x141313);
const _tmpColor = new THREE.Color();

/** crash damage from an impact speed (m/s) */
function crashDamage(impact: number): number {
  if (impact <= 2.5) return 0;
  return Math.pow(impact - 2.5, 1.3) * 0.9;
}

// ------------------------------------------------- player headlight beams ----
// Two real SpotLights shared by whatever vehicle the player drives. Created once and kept in
// the scene at intensity 0 so the light count never changes (no shader recompiles).
interface Beams {
  lights: THREE.SpotLight[];
  owner: Vehicle | null;
}
let beams: Beams | null = null;

function getBeams(): Beams {
  if (beams) return beams;
  const lights: THREE.SpotLight[] = [];
  for (let i = 0; i < 2; i++) {
    const s = new THREE.SpotLight(0xfff1cf, 0, 70, 0.48, 0.55, 1.7);
    s.castShadow = false;
    s.target = new THREE.Object3D();
    G.scene.add(s);
    G.scene.add(s.target);
    lights.push(s);
  }
  beams = { lights, owner: null };
  return beams;
}

function attachBeams(v: Vehicle): void {
  const b = getBeams();
  if (b.owner === v) return;
  if (b.owner) detachBeams(b.owner);
  const hp = v.tpl.headlightPos;
  for (let i = 0; i < 2; i++) {
    const s = b.lights[i];
    const side = i === 0 ? 1 : -1;
    v.group.add(s);
    v.group.add(s.target);
    s.position.set(side * hp.x, hp.y, hp.z - 0.1);
    s.target.position.set(side * (hp.x + 0.6), -0.9, hp.z + 26);
  }
  b.owner = v;
}

function detachBeams(v: Vehicle): void {
  const b = beams;
  if (!b || b.owner !== v) return;
  for (const s of b.lights) {
    s.intensity = 0;
    G.scene.add(s);
    G.scene.add(s.target);
    s.position.set(0, -50, 0);
  }
  b.owner = null;
}

// ------------------------------------------------------------------ class ----

export class Vehicle implements IVehicle {
  readonly id = nextId++;
  readonly spec: VehicleSpec;
  readonly tpl: VehicleTemplate;
  group = new THREE.Group();
  pos = new THREE.Vector3();
  heading = 0;
  speed = 0;
  velocity = new THREE.Vector3();
  health: number;
  destroyed = false;
  driver: 'player' | IPed | null = null;
  passengers: IPed[] = [];
  controls: VehicleControls = { throttle: 0, steer: 0, handbrake: false, horn: false };
  parked = false;
  persistent = false;
  sirenOn = false;
  playerTouched = false;
  lastPlayerContact = -1e9;

  // ---- extra public state (not in the contract, read-only for other modules)
  /** headlights currently on */
  lightsOn = false;
  /** brake lights lit this frame */
  braking = false;
  /** reversing lights lit this frame */
  reversing = false;
  /** current (smoothed) front wheel angle, radians, + = right */
  steerAngle = 0;
  /** true while the tyres are sliding sideways (drift) */
  drifting = false;
  /** who dealt the last damage (used as the explosion's source) */
  lastDamageSource: DamageSource = 'other';
  /** body colour as spawned */
  readonly bodyColor: number;
  /** world-space collision circle centres (updated every frame) + radius */
  colX: number[];
  colZ: number[];
  colR: number;
  /** extra yaw velocity from impacts (rad/s), decays */
  yawVel = 0;

  // ---- private
  private bodyGroup = new THREE.Group();
  private wheelMeshes: THREE.Mesh[] = [];
  private bodyMat: THREE.MeshStandardMaterial;
  private headMat: THREE.MeshStandardMaterial;
  private tailMat: THREE.MeshStandardMaterial;
  private lensRed: THREE.MeshStandardMaterial | null = null;
  private lensBlue: THREE.MeshStandardMaterial | null = null;
  private taxiSignMat: THREE.MeshStandardMaterial | null = null;
  private instanceMats: THREE.Material[] = [];
  private wheelSpin = 0;
  private bodyPitch = 0;
  private bodyRoll = 0;
  private pitchVel = 0;
  private rollVel = 0;
  private bobPhase = 0;
  private lastHorn = -10;
  private lastScreech = -10;
  private lastDust = -10;
  private lastSmoke = -10;
  private lastFire = -10;
  private lastCrash = -10;
  private lastWallDamage = -10;
  private destroyedAt = 0;
  private tailState = -1;
  private headState = -1;
  private tailReverseColor = false;
  private hdot = 0;
  private lastLong = 0;

  constructor(
    readonly type: VehicleType,
    x: number,
    z: number,
    heading: number,
    color?: number,
  ) {
    this.spec = VEHICLE_SPECS[type];
    this.health = this.spec.health;
    this.pos.set(x, 0, z);
    this.heading = heading;
    const tpl = getVehicleTemplate(type);
    this.tpl = tpl;
    const M = vehicleMats();

    this.bodyColor = color ?? tpl.defaultColor ?? CAR_COLORS[Math.floor(G.rng() * CAR_COLORS.length)];
    this.bodyMat = M.body.clone();
    this.bodyMat.color.setHex(this.bodyColor);
    this.headMat = M.head.clone();
    this.tailMat = M.tail.clone();
    this.instanceMats.push(this.bodyMat, this.headMat, this.tailMat);

    const body = new THREE.Mesh(tpl.body, this.bodyMat);
    body.castShadow = true;
    body.receiveShadow = true;
    const trim = new THREE.Mesh(tpl.trim, M.trim);
    trim.castShadow = true;
    const glass = new THREE.Mesh(tpl.glass, M.glass);
    const head = new THREE.Mesh(tpl.head, this.headMat);
    const tail = new THREE.Mesh(tpl.tail, this.tailMat);
    this.bodyGroup.add(body, trim, glass, head, tail);
    for (const ex of tpl.extras) {
      let mat: THREE.Material = ex.mat;
      if (ex.kind === 'lensRed') {
        this.lensRed = (ex.mat as THREE.MeshStandardMaterial).clone();
        mat = this.lensRed;
      } else if (ex.kind === 'lensBlue') {
        this.lensBlue = (ex.mat as THREE.MeshStandardMaterial).clone();
        mat = this.lensBlue;
      } else if (ex.kind === 'taxiSign') {
        this.taxiSignMat = (ex.mat as THREE.MeshStandardMaterial).clone();
        mat = this.taxiSignMat;
      }
      if (mat !== ex.mat) this.instanceMats.push(mat);
      const m = new THREE.Mesh(ex.geo, mat);
      m.castShadow = ex.castShadow;
      m.receiveShadow = ex.castShadow;
      this.bodyGroup.add(m);
    }
    this.group.add(this.bodyGroup);

    for (const w of tpl.wheels) {
      const m = new THREE.Mesh(tpl.wheelGeo, M.wheel);
      m.castShadow = true;
      m.receiveShadow = true;
      m.rotation.order = 'YXZ';
      m.position.set(w.x, tpl.wheelRadius, w.z);
      this.group.add(m);
      this.wheelMeshes.push(m);
    }

    this.colR = tpl.colR;
    this.colX = new Array(tpl.colZ.length).fill(x);
    this.colZ = new Array(tpl.colZ.length).fill(z);
    // a spawn that overlaps a prop / wall self-heals once (asleep cars skip collision later)
    if (G.city) this.resolveCity(0);
    this.updateCollisionCircles();

    this.group.userData.entity = this;
    this.group.userData.kind = 'vehicle';
    this.group.position.copy(this.pos);
    this.group.rotation.y = heading;
    this.applyLightState();
  }

  // ------------------------------------------------------------ helpers ----

  /** forward unit vector on XZ */
  forward(out: THREE.Vector3): THREE.Vector3 {
    return out.set(Math.sin(this.heading), 0, Math.cos(this.heading));
  }

  /** local (x = left, z = forward) -> world (writes out, y = ly) */
  private localToWorld2(lx: number, ly: number, lz: number, out: THREE.Vector3): THREE.Vector3 {
    const s = Math.sin(this.heading), c = Math.cos(this.heading);
    return out.set(this.pos.x + lx * c + lz * s, ly, this.pos.z - lx * s + lz * c);
  }

  /** recompute world-space collision circles from pos/heading (internal, also used by the pair resolver) */
  updateCollisionCircles(): void {
    const s = Math.sin(this.heading), c = Math.cos(this.heading);
    const cz = this.tpl.colZ;
    for (let i = 0; i < cz.length; i++) {
      this.colX[i] = this.pos.x + cz[i] * s;
      this.colZ[i] = this.pos.z + cz[i] * c;
    }
  }

  /** crash reaction for car vs car (internal): bounce + damage + event; audio/sparks are emitted once per pair by the resolver */
  crashFromVehicle(impact: number, dmg: number, source: DamageSource): void {
    const t = G.time.t;
    this.pitchVel -= Math.min(impact, 25) * 0.08;
    this.rollVel += (G.rng() - 0.5) * Math.min(impact, 25) * 0.06;
    if (dmg > 0 && t - this.lastWallDamage > 0.2) {
      this.lastWallDamage = t;
      this.applyDamage(dmg, source);
    }
    if (t - this.lastCrash > 0.25) {
      this.lastCrash = t;
      G.events.emit('vehicleCrash', { vehicle: this, speed: impact, x: this.pos.x, z: this.pos.z });
    }
  }

  // ------------------------------------------------------------- lights ----

  setLights(on: boolean): void {
    if (this.lightsOn === on) return;
    this.lightsOn = on && !this.destroyed;
    this.applyLightState();
  }

  private applyLightState(): void {
    const on = this.lightsOn && !this.destroyed;
    const hs = on ? 1 : 0;
    if (hs !== this.headState) {
      this.headState = hs;
      this.headMat.emissiveIntensity = on ? HEAD_ON : 0;
      if (this.taxiSignMat) this.taxiSignMat.emissiveIntensity = on ? 1.1 : 0;
    }
    let ts = 0;
    if (!this.destroyed) {
      if (this.reversing) ts = 3;
      else if (this.braking) ts = 2;
      else if (on) ts = 1;
    }
    if (ts !== this.tailState) {
      this.tailState = ts;
      this.tailMat.emissiveIntensity = ts === 3 ? TAIL_REVERSE : ts === 2 ? TAIL_BRAKE : ts === 1 ? TAIL_ON : 0;
      const rev = ts === 3;
      if (rev !== this.tailReverseColor) {
        this.tailReverseColor = rev;
        this.tailMat.emissive.copy(rev ? _tailWhite : _tailRed);
      }
    }
    if (beams && beams.owner === this) {
      const i = on ? 160 : 0;
      beams.lights[0].intensity = i;
      beams.lights[1].intensity = i;
    }
  }

  private updateSiren(t: number): void {
    if (!this.lensRed || !this.lensBlue) return;
    if (this.sirenOn && !this.destroyed) {
      const ph = (t * 3.2) % 1;
      const redOn = ph < 0.5;
      const fast = (t * 19) % 1 < 0.55; // strobe inside each half
      this.lensRed.emissiveIntensity = redOn && fast ? 4.5 : 0.15;
      this.lensBlue.emissiveIntensity = !redOn && fast ? 4.5 : 0.15;
    } else if (this.lensRed.emissiveIntensity !== 0.1) {
      this.lensRed.emissiveIntensity = 0.1;
      this.lensBlue.emissiveIntensity = 0.1;
    }
  }

  // ------------------------------------------------------------- update ----

  update(dt: number): void {
    if (dt > MAX_DT) dt = MAX_DT;
    if (dt <= 0) return;
    const t = G.time.t;
    const s = this.spec;
    const tpl = this.tpl;

    if (this.driver === 'player') this.lastPlayerContact = t;

    if (this.destroyed) {
      this.speed = 0;
      this.velocity.set(0, 0, 0);
      this.updateDamageFx(t);
      this.settleBody(dt);
      this.syncTransform();
      return;
    }

    // ---- inputs
    const c = this.controls;
    let throttle = clamp(c.throttle, -1, 1);
    let steer = clamp(c.steer, -1, 1);
    let hb = !!c.handbrake;
    const noDriver = this.driver === null;
    if (noDriver) {
      throttle = 0;
      steer = 0;
      hb = true;
    }
    if (!Number.isFinite(throttle)) throttle = 0;
    if (!Number.isFinite(steer)) steer = 0;

    // horn
    if (c.horn && !noDriver && t - this.lastHorn > HORN_COOLDOWN) {
      this.lastHorn = t;
      G.audio.play('horn', this.pos.x, this.pos.z, 0.9);
    }

    // ---- decompose velocity in the car frame
    const sh = Math.sin(this.heading), ch = Math.cos(this.heading);
    let vf = this.velocity.x * sh + this.velocity.z * ch; // forward
    let vl = this.velocity.x * ch - this.velocity.z * sh; // left
    const absVf = Math.abs(vf);

    // asleep: nobody inside, not moving -> nothing to integrate
    const asleep = noDriver && absVf < 0.02 && Math.abs(vl) < 0.02 && Math.abs(this.yawVel) < 0.01;
    if (asleep) {
      this.velocity.set(0, 0, 0);
      this.speed = 0;
      this.yawVel = 0;
      this.braking = false;
      this.reversing = false;
      this.drifting = false;
      this.steerAngle = damp(this.steerAngle, 0, 6, dt);
      this.updateSiren(t);
      this.updateDamageFx(t);
      this.applyLightState();
      this.settleBody(dt);
      this.syncTransform();
      return;
    }

    // ---- longitudinal
    let a = 0;
    this.braking = false;
    this.reversing = false;
    if (throttle > 0.01) {
      if (vf < -0.3) {
        a = s.brake * throttle;
        this.braking = true;
      } else {
        const k = clamp(vf / s.maxSpeed, 0, 1);
        a = s.accel * throttle * (1 - 0.65 * k * k);
      }
    } else if (throttle < -0.01) {
      if (vf > 0.3) {
        a = s.brake * throttle; // negative
        this.braking = true;
      } else {
        const rmax = s.maxSpeed / 3;
        const k = clamp(-vf / rmax, 0, 1);
        a = s.accel * 0.7 * throttle * (1 - 0.6 * k * k);
        this.reversing = true;
      }
    }
    let vf1 = vf + a * dt;
    // drag: air (v^2, equilibrium at maxSpeed), rolling, engine braking, handbrake
    const cAir = (0.35 * s.accel) / (s.maxSpeed * s.maxSpeed);
    let dragMag = cAir * vf1 * vf1 + 0.6;
    if (Math.abs(throttle) < 0.01) dragMag += 1.6 + 0.05 * Math.abs(vf1);
    if (hb) {
      dragMag += s.brake * 0.4;
      if (absVf > 1) this.braking = true;
    }
    const dv = Math.min(Math.abs(vf1), dragMag * dt);
    vf1 -= Math.sign(vf1) * dv;
    vf1 = clamp(vf1, -s.maxSpeed / 3, s.maxSpeed * 1.02);
    this.lastLong = (vf1 - vf) / dt;

    // ---- steering (bicycle model, lock reduced with speed)
    const absV1 = Math.abs(vf1);
    const sf = absV1 < 8 ? 1 : clamp(1 - (0.65 * (absV1 - 8)) / Math.max(1, s.maxSpeed - 8), 0.35, 1);
    const target = steer * s.steerLock * sf;
    this.steerAngle = damp(this.steerAngle, target, 9, dt);
    let yawRate = (vf1 * Math.tan(this.steerAngle)) / tpl.wheelbase;
    // the tyres can only deliver so much lateral acceleration: cap the yaw rate (understeer)
    // unless the handbrake is pulled, which lets the rear step out (oversteer / spin)
    const aLatMax = LAT_ACCEL * s.grip * (hb ? 2.6 : 1);
    const yawCap = aLatMax / Math.max(absV1, 1);
    if (Math.abs(yawRate) > yawCap) yawRate = Math.sign(yawRate) * yawCap;
    if (hb && absV1 > 4) yawRate *= 1.3;

    // ---- lateral grip
    let g = s.grip;
    if (hb) g *= 0.2;
    const slip = Math.abs(vl);
    if (slip > 4.5) g *= lerp(1, 0.45, clamp((slip - 4.5) / 8, 0, 1));
    const rate = 1.2 + 18 * g * g;
    vl *= Math.exp(-rate * dt);
    this.drifting = slip > 3.2 && absV1 > 3;

    // recompose in the OLD frame (velocity lags the body rotation -> natural slip)
    this.velocity.set(sh * vf1 + ch * vl, 0, ch * vf1 - sh * vl);

    // ---- rotate + move
    const hdot = -yawRate + this.yawVel;
    this.hdot = hdot;
    this.heading = wrapAngle(this.heading + hdot * dt);
    this.yawVel *= Math.exp(-3.5 * dt);
    if (Math.abs(this.yawVel) < 1e-4) this.yawVel = 0;
    this.pos.x += this.velocity.x * dt;
    this.pos.z += this.velocity.z * dt;

    // ---- city collisions (circles along the length)
    this.resolveCity(dt);

    // ---- bounds
    const b = G.city.bounds;
    if (this.pos.x < b.minX + 1) {
      this.pos.x = b.minX + 1;
      if (this.velocity.x < 0) this.velocity.x = 0;
    } else if (this.pos.x > b.maxX - 1) {
      this.pos.x = b.maxX - 1;
      if (this.velocity.x > 0) this.velocity.x = 0;
    }
    if (this.pos.z < b.minZ + 1) {
      this.pos.z = b.minZ + 1;
      if (this.velocity.z < 0) this.velocity.z = 0;
    } else if (this.pos.z > b.maxZ - 1) {
      this.pos.z = b.maxZ - 1;
      if (this.velocity.z > 0) this.velocity.z = 0;
    }

    // NaN guard (never let a bad frame poison the state)
    if (!Number.isFinite(this.pos.x) || !Number.isFinite(this.pos.z) || !Number.isFinite(this.velocity.x) || !Number.isFinite(this.velocity.z) || !Number.isFinite(this.heading)) {
      this.velocity.set(0, 0, 0);
      this.yawVel = 0;
      if (!Number.isFinite(this.heading)) this.heading = 0;
      if (!Number.isFinite(this.pos.x)) this.pos.x = 0;
      if (!Number.isFinite(this.pos.z)) this.pos.z = 0;
    }

    // signed speed along the (new) heading
    const sh2 = Math.sin(this.heading), ch2 = Math.cos(this.heading);
    this.speed = this.velocity.x * sh2 + this.velocity.z * ch2;
    this.updateCollisionCircles();

    // ---- tyre fx
    this.wheelSpin += (this.speed / tpl.wheelRadius) * dt;
    if (this.wheelSpin > 1000) this.wheelSpin -= 1000;
    const vlNow = this.velocity.x * ch2 - this.velocity.z * sh2;
    const slipNow = Math.abs(vlNow);
    const skid = this.braking && !hb && Math.abs(this.speed) > 13 && Math.abs(throttle) > 0.7;
    if ((slipNow > 3.2 && Math.abs(this.speed) > 3) || (hb && Math.abs(this.speed) > 6) || skid) {
      if (t - this.lastDust > 0.06) {
        this.lastDust = t;
        const w = tpl.wheels;
        for (let i = w.length - 2; i < w.length; i++) {
          this.localToWorld2(w[i].x, 0.15, w[i].z, _v);
          G.particles.emit('dust', _v.x, _v.y, _v.z, 2);
        }
      }
      if (t - this.lastScreech > 0.32) {
        this.lastScreech = t;
        G.audio.play('tyre_screech', this.pos.x, this.pos.z, clamp(0.35 + slipNow / 10, 0.35, 1));
      }
    }

    // ---- visuals
    this.updateSiren(t);
    this.updateDamageFx(t);
    this.applyLightState();
    this.updateBody(dt);
    this.syncTransform();
  }

  private resolveCity(dt: number): void {
    const city = G.city;
    const tpl = this.tpl;
    const r = this.colR;
    const cz = tpl.colZ;
    const mass = this.spec.mass;
    const inertia = (mass * (this.spec.length * this.spec.length + this.spec.width * this.spec.width)) / 12;
    let maxImpact = 0;
    let anyHit = false;
    for (let i = 0; i < cz.length; i++) {
      const s = Math.sin(this.heading), c = Math.cos(this.heading);
      const cx = this.pos.x + cz[i] * s;
      const czz = this.pos.z + cz[i] * c;
      const hit = city.resolveCircle(cx, czz, r);
      if (!hit) continue;
      anyHit = true;
      this.pos.x += hit.x - cx;
      this.pos.z += hit.z - czz;
      const vn = this.velocity.x * hit.nx + this.velocity.z * hit.nz;
      if (vn < 0) {
        const impact = -vn;
        const j = impact * (1 + RESTITUTION_WALL);
        this.velocity.x += hit.nx * j;
        this.velocity.z += hit.nz * j;
        // angular impulse: lever from centre to circle centre, force along n
        const lx = cz[i] * s, lz = cz[i] * c;
        const torque = (lz * hit.nx - lx * hit.nz) * impact * mass;
        this.yawVel += (torque / inertia) * WALL_TORQUE;
        if (impact > maxImpact) maxImpact = impact;
        // wall friction: scrub some tangential speed
        const tx = -hit.nz, tz = hit.nx;
        const vt = this.velocity.x * tx + this.velocity.z * tz;
        const f = Math.min(Math.abs(vt), 4 * dt) * Math.sign(vt);
        this.velocity.x -= tx * f;
        this.velocity.z -= tz * f;
      }
    }
    if (anyHit && maxImpact > CRASH_MIN) this.onCrash(maxImpact, 'wall');
  }

  /** shared crash reaction (wall or vehicle). `impact` in m/s along the contact normal. */
  onCrash(impact: number, kind: 'wall' | 'vehicle', source: DamageSource = 'other'): void {
    const t = G.time.t;
    const x = this.pos.x, z = this.pos.z;
    // body bounce
    this.pitchVel -= Math.min(impact, 25) * 0.09;
    this.rollVel += (G.rng() - 0.5) * Math.min(impact, 25) * 0.05;
    const dmg = crashDamage(impact);
    if (dmg > 0 && t - this.lastWallDamage > 0.2) {
      this.lastWallDamage = t;
      const src: DamageSource = kind === 'wall' ? (this.driver === 'player' ? 'player' : 'other') : source;
      this.applyDamage(dmg, src);
    }
    if (t - this.lastCrash > 0.25) {
      this.lastCrash = t;
      G.events.emit('vehicleCrash', { vehicle: this, speed: impact, x, z });
      G.audio.play('crash', x, z, clamp(impact / 22, 0.15, 1));
      if (impact > 7) {
        this.localToWorld2(0, 0.5, kind === 'wall' && this.speed >= 0 ? this.spec.length * 0.45 : 0, _v);
        G.particles.emit('spark', _v.x, _v.y, _v.z, Math.min(24, Math.round(impact * 1.2)));
      }
      if (this.driver === 'player') G.cam.shake(clamp(impact * 0.018, 0.05, 0.6), 0.35);
    }
  }

  // ------------------------------------------------------------- visuals ----

  private settleBody(dt: number): void {
    // damped spring back to rest (used when asleep / destroyed)
    this.pitchVel += (-70 * this.bodyPitch - 9 * this.pitchVel) * dt;
    this.bodyPitch += this.pitchVel * dt;
    this.rollVel += (-70 * this.bodyRoll - 9 * this.rollVel) * dt;
    this.bodyRoll += this.rollVel * dt;
    const bg = this.bodyGroup;
    bg.rotation.x = this.bodyPitch;
    bg.rotation.z = this.bodyRoll;
    bg.position.y = damp(bg.position.y, this.destroyed ? -0.06 : 0, 4, dt);
    for (const w of this.wheelMeshes) w.rotation.y = damp(w.rotation.y, 0, 6, dt);
  }

  private updateBody(dt: number): void {
    const targetPitch = clamp(this.lastLong * 0.0045, -0.055, 0.055);
    const targetRoll = clamp(this.hdot * this.speed * 0.011, -0.075, 0.075);
    this.pitchVel += (-70 * (this.bodyPitch - targetPitch) - 9 * this.pitchVel) * dt;
    this.bodyPitch += this.pitchVel * dt;
    this.rollVel += (-70 * (this.bodyRoll - targetRoll) - 9 * this.rollVel) * dt;
    this.bodyRoll += this.rollVel * dt;
    this.bobPhase += Math.abs(this.speed) * dt * 1.7;
    const bob = 0.009 * Math.sin(this.bobPhase) * clamp(Math.abs(this.speed) / 8, 0, 1);
    const bg = this.bodyGroup;
    bg.rotation.x = this.bodyPitch;
    bg.rotation.z = this.bodyRoll;
    bg.position.y = bob;
    const wheels = this.tpl.wheels;
    for (let i = 0; i < this.wheelMeshes.length; i++) {
      const m = this.wheelMeshes[i];
      m.rotation.x = this.wheelSpin;
      m.rotation.y = wheels[i].steer ? -this.steerAngle : 0;
    }
  }

  private syncTransform(): void {
    this.group.position.set(this.pos.x, 0, this.pos.z);
    this.group.rotation.y = this.heading;
  }

  private updateDamageFx(t: number): void {
    const frac = this.health / this.spec.health;
    if (this.destroyed) {
      if (t - this.destroyedAt < 30 && t - this.lastSmoke > 0.22) {
        this.lastSmoke = t;
        this.localToWorld2((G.rng() - 0.5) * 0.8, 0.9, (G.rng() - 0.5) * 1.5, _v);
        G.particles.emit('smoke', _v.x, _v.y, _v.z, 1);
        if (t - this.destroyedAt < 12 && t - this.lastFire > 0.3) {
          this.lastFire = t;
          G.particles.emit('fire', _v.x, _v.y - 0.3, _v.z, 1);
        }
      }
      return;
    }
    if (frac < 0.55 && t - this.lastSmoke > (frac < 0.25 ? 0.08 : 0.15)) {
      this.lastSmoke = t;
      const b = this.tpl.bonnet;
      this.localToWorld2((G.rng() - 0.5) * 0.5, b.y + 0.05, b.z + (G.rng() - 0.5) * 0.3, _v);
      G.particles.emit('smoke', _v.x, _v.y, _v.z, 1);
    }
    if (frac < 0.25 && t - this.lastFire > 0.09) {
      this.lastFire = t;
      const b = this.tpl.bonnet;
      this.localToWorld2((G.rng() - 0.5) * 0.4, b.y, b.z + (G.rng() - 0.5) * 0.3, _v);
      G.particles.emit('fire', _v.x, _v.y, _v.z, 1);
    }
  }

  private refreshDamageLook(): void {
    const frac = clamp(this.health / this.spec.health, 0, 1);
    const dmg = 1 - frac;
    _tmpColor.setHex(this.bodyColor);
    _tmpColor.multiplyScalar(lerp(1, 0.6, dmg));
    this.bodyMat.color.copy(_tmpColor);
    this.bodyMat.roughness = lerp(0.5, 0.78, dmg);
  }

  // ------------------------------------------------------------- damage ----

  applyDamage(amount: number, source: DamageSource): void {
    if (this.destroyed || !(amount > 0)) return;
    this.health -= amount;
    this.lastDamageSource = source;
    if (this.health <= 0) {
      this.health = 0;
      this.explode();
      return;
    }
    this.refreshDamageLook();
  }

  explode(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.health = 0;
    this.destroyedAt = G.time.t;
    this.speed = 0;
    this.velocity.set(0, 0, 0);
    this.yawVel = 0;
    this.sirenOn = false;
    this.controls.throttle = 0;
    this.controls.steer = 0;
    this.pitchVel += 1.2;
    this.rollVel += (G.rng() - 0.5) * 1.5;
    const x = this.pos.x, z = this.pos.z;
    const src = this.lastDamageSource;

    // look: charred body, dead glass, lights off
    this.bodyMat.color.copy(_charred);
    this.bodyMat.roughness = 0.92;
    this.bodyMat.metalness = 0.05;
    this.headMat.emissiveIntensity = 0;
    this.headMat.color.setHex(0x2a2a2a);
    this.tailMat.emissiveIntensity = 0;
    this.tailMat.color.setHex(0x2a1a1a);
    if (this.taxiSignMat) this.taxiSignMat.emissiveIntensity = 0;
    if (this.lensRed) this.lensRed.emissiveIntensity = 0;
    if (this.lensBlue) this.lensBlue.emissiveIntensity = 0;
    this.lightsOn = false;
    this.headState = 0;
    this.tailState = 0;
    if (beams && beams.owner === this) detachBeams(this);

    // fx + area damage
    const size = clamp(this.spec.length / 4.5, 1, 2.2);
    G.particles.explosion(x, 1, z, size);
    G.events.emit('explosion', { x, y: 1, z, size });
    G.weapons.explosionAt(x, 1, z, 7, 90, src);

    // occupants
    if (this.driver && this.driver !== 'player') this.driver.applyDamage(1000, 'explosion');
    for (const p of this.passengers) p.applyDamage(1000, 'explosion');
    if (G.player.vehicle === this) G.player.applyDamage(1000, 'explosion');

    G.events.emit('vehicleDestroyed', { vehicle: this, source: src });
    if (src === 'player') G.events.emit('crime', { kind: 'vehicle_destroyed', x, z });
  }

  // ---------------------------------------------------------- positions ----

  seatPosition(index: number, out: THREE.Vector3): THREE.Vector3 {
    const seats = this.tpl.seats;
    const s = seats[clamp(Math.floor(index), 0, seats.length - 1)];
    return this.localToWorld2(s.x, s.y, s.z, out);
  }

  doorPosition(out: THREE.Vector3): THREE.Vector3 {
    return this.localToWorld2(this.spec.width / 2 + 0.9, 0, this.tpl.doorZ, out);
  }

  containsPoint(x: number, z: number, margin: number): boolean {
    const dx = x - this.pos.x, dz = z - this.pos.z;
    const s = Math.sin(this.heading), c = Math.cos(this.heading);
    const fwd = dx * s + dz * c;
    const side = dx * c - dz * s;
    return Math.abs(fwd) < this.spec.length / 2 + margin && Math.abs(side) < this.spec.width / 2 + margin;
  }

  dispose(): void {
    if (beams && beams.owner === this) detachBeams(this);
    for (const m of this.instanceMats) m.dispose();
    this.instanceMats.length = 0;
    this.wheelMeshes.length = 0;
    this.group.clear();
  }

  /** called by update loop: make sure the player's real headlight beams follow this car */
  syncPlayerBeams(): void {
    if (this.driver === 'player' && !this.destroyed) {
      if (!beams || beams.owner !== this) {
        attachBeams(this);
        this.headState = -1; // force intensity refresh
        this.applyLightState();
      }
    } else if (beams && beams.owner === this) {
      detachBeams(this);
    }
  }
}

// --------------------------------------------------- vehicle vs vehicle ----

const _hitA = new THREE.Vector3();

/** push overlapping vehicles apart + exchange momentum (called once per frame by main). */
export function resolveVehicleCollisions(vehicles: IVehicle[]): void {
  const n = vehicles.length;
  const t = G.time.t;
  for (let i = 0; i < n; i++) {
    const a = vehicles[i] as Vehicle;
    if (!(a instanceof Vehicle)) continue;
    a.syncPlayerBeams();
    const aStatic = a.destroyed;
    const aMoving = a.velocity.x !== 0 || a.velocity.z !== 0;
    const ra = a.spec.length * 0.5 + 0.6;
    for (let j = i + 1; j < n; j++) {
      const b = vehicles[j] as Vehicle;
      if (!(b instanceof Vehicle)) continue;
      const bStatic = b.destroyed;
      if (aStatic && bStatic) continue;
      const bMoving = b.velocity.x !== 0 || b.velocity.z !== 0;
      if (!aMoving && !bMoving && (a.parked || a.driver === null) && (b.parked || b.driver === null)) continue;
      // cheap reject
      const dx = b.pos.x - a.pos.x, dz = b.pos.z - a.pos.z;
      const rb = b.spec.length * 0.5 + 0.6;
      if (dx * dx + dz * dz > (ra + rb) * (ra + rb)) continue;

      // circle pairs
      const ca = a.colX.length, cb = b.colX.length;
      const rr = a.colR + b.colR;
      let bestOverlap = 0, bi = -1, bj = -1, bnx = 0, bnz = 0;
      for (let p = 0; p < ca; p++) {
        for (let q = 0; q < cb; q++) {
          const ex = b.colX[q] - a.colX[p], ez = b.colZ[q] - a.colZ[p];
          const d2 = ex * ex + ez * ez;
          if (d2 >= rr * rr) continue;
          const d = Math.sqrt(d2);
          const overlap = rr - d;
          if (overlap > bestOverlap) {
            bestOverlap = overlap;
            bi = p;
            bj = q;
            if (d > 1e-4) {
              bnx = ex / d;
              bnz = ez / d;
            } else {
              // coincident: push along a's left axis
              bnx = Math.cos(a.heading);
              bnz = -Math.sin(a.heading);
            }
          }
        }
      }
      if (bi < 0) continue;

      const ma = aStatic ? Infinity : a.spec.mass;
      const mb = bStatic ? Infinity : b.spec.mass;
      const invA = aStatic ? 0 : 1 / ma;
      const invB = bStatic ? 0 : 1 / mb;
      const invSum = invA + invB;
      if (invSum === 0) continue;

      // separate (weighted by inverse mass)
      const push = bestOverlap + 0.01;
      const wa = invA / invSum, wb = invB / invSum;
      a.pos.x -= bnx * push * wa;
      a.pos.z -= bnz * push * wa;
      b.pos.x += bnx * push * wb;
      b.pos.z += bnz * push * wb;

      // momentum exchange along the normal
      const rvx = b.velocity.x - a.velocity.x, rvz = b.velocity.z - a.velocity.z;
      const vrel = rvx * bnx + rvz * bnz;
      let impact = 0;
      if (vrel < 0) {
        impact = -vrel;
        const jn = (-(1 + RESTITUTION_CAR) * vrel) / invSum;
        a.velocity.x -= jn * invA * bnx;
        a.velocity.z -= jn * invA * bnz;
        b.velocity.x += jn * invB * bnx;
        b.velocity.z += jn * invB * bnz;
        // angular impulses (lever = circle centre offset from the car centre)
        const sa = Math.sin(a.heading), caH = Math.cos(a.heading);
        const laX = a.tpl.colZ[bi] * sa, laZ = a.tpl.colZ[bi] * caH;
        const ia = (a.spec.mass * (a.spec.length * a.spec.length + a.spec.width * a.spec.width)) / 12;
        if (!aStatic) a.yawVel += ((laZ * -bnx - laX * -bnz) * jn * CAR_TORQUE) / ia;
        const sb = Math.sin(b.heading), cbH = Math.cos(b.heading);
        const lbX = b.tpl.colZ[bj] * sb, lbZ = b.tpl.colZ[bj] * cbH;
        const ib = (b.spec.mass * (b.spec.length * b.spec.length + b.spec.width * b.spec.width)) / 12;
        if (!bStatic) b.yawVel += ((lbZ * bnx - lbX * bnz) * jn * CAR_TORQUE) / ib;
      }
      // friction along the tangent (scrubs sideways scraping)
      const tx = -bnz, tz = bnx;
      const vt = rvx * tx + rvz * tz;
      const jt = clamp(-vt * 0.25, -3, 3) / invSum;
      a.velocity.x -= jt * invA * tx;
      a.velocity.z -= jt * invA * tz;
      b.velocity.x += jt * invB * tx;
      b.velocity.z += jt * invB * tz;

      a.updateCollisionCircles();
      b.updateCollisionCircles();
      a.group.position.set(a.pos.x, 0, a.pos.z);
      b.group.position.set(b.pos.x, 0, b.pos.z);
      // wake parked cars that got shoved
      if (a.parked && (a.velocity.x !== 0 || a.velocity.z !== 0)) a.speed = a.velocity.x * Math.sin(a.heading) + a.velocity.z * Math.cos(a.heading);
      if (b.parked && (b.velocity.x !== 0 || b.velocity.z !== 0)) b.speed = b.velocity.x * Math.sin(b.heading) + b.velocity.z * Math.cos(b.heading);

      if (impact > CRASH_MIN) {
        // damage scaled by the other's mass (a truck hurts a hatch more than the reverse)
        const dmgBase = crashDamage(impact);
        const srcForA: DamageSource = b.driver === 'player' ? 'player' : 'vehicle';
        const srcForB: DamageSource = a.driver === 'player' ? 'player' : 'vehicle';
        if (!aStatic) a.crashFromVehicle(impact, dmgBase * clamp(b.spec.mass / a.spec.mass, 0.5, 2.5), srcForA);
        if (!bStatic) b.crashFromVehicle(impact, dmgBase * clamp(a.spec.mass / b.spec.mass, 0.5, 2.5), srcForB);
        // shared fx at the contact point
        if (t - lastPairFx > 0.12) {
          lastPairFx = t;
          _hitA.set(a.colX[bi] + bnx * a.colR, 0.6, a.colZ[bi] + bnz * a.colR);
          G.audio.play('crash', _hitA.x, _hitA.z, clamp(impact / 20, 0.2, 1));
          if (impact > 6) G.particles.emit('spark', _hitA.x, _hitA.y, _hitA.z, Math.min(30, Math.round(impact * 1.5)));
          if (a.driver === 'player' || b.driver === 'player') G.cam.shake(clamp(impact * 0.02, 0.05, 0.7), 0.4);
        }
      }
    }
  }
}
let lastPairFx = -10;
